import { assert, decodeFromBase64, getNonEmptyArrayLastElement, objectKeys, type ReadonlyRecord } from '../../../utils'

import {
    Capability,
    ConnectwareError,
    ConnectwareErrorType,
    type CybusCatalogApplication,
    CybusCatalogApplicationCategory,
    type CybusCatalogServiceConfiguration,
    type CybusCatalogServiceVersionData,
} from '../../../domain'

import { type ConfigurationService, type ConnectwareServicesCatalogService, FileExtensionType, type LoggerService } from '../../../application'

import { FetchConnectwareHTTPService } from '../Base'

const serviceCategoriesMap: ReadonlyRecord<string, CybusCatalogApplicationCategory> = {
    Applications: CybusCatalogApplicationCategory.APPLICATIONS,
    'Northbound Connectivity': CybusCatalogApplicationCategory.NORTHBOUND,
    'Southbound Connectivity': CybusCatalogApplicationCategory.SOUTHBOUND,
}

export class ConnectwareHTTPServicesCatalogService extends FetchConnectwareHTTPService implements ConnectwareServicesCatalogService {
    private readonly configuration: ConfigurationService
    private readonly logger: LoggerService

    constructor (
        ...[options, configuration, logger]: [
            ...baseOptions: ConstructorParameters<typeof FetchConnectwareHTTPService>,
            configuration: ConnectwareHTTPServicesCatalogService['configuration'],
            logger: ConnectwareHTTPServicesCatalogService['logger']
        ]
    ) {
        super(options)

        this.configuration = configuration
        this.logger = logger
    }

    fetchCatalogData (queryParams: CybusCatalogServiceConfiguration): Promise<CybusCatalogServiceVersionData> {
        return this.request<'/api/marketplace/app/meta', 'GET', CybusCatalogServiceVersionData>({
            capability: Capability.SERVICES_CATALOG_READ,
            method: 'GET',
            path: '/api/marketplace/app/meta',
            authenticate: true,
            queryParams,
            handlers: {
                200: async (response) => {
                    const { meta, content } = await response.getJson()

                    return { version: meta.version, updatedAt: new Date(meta.updatedAt), commissioningFile: decodeFromBase64(content.data) }
                },
                400: () => new ConnectwareError(ConnectwareErrorType.BAD_REQUEST, 'Unexpected Bad Request detected'),
            },
        })
    }

    fetchCatalogApplications (): Promise<readonly CybusCatalogApplication[]> {
        return this.request<'/api/marketplace/apps', 'GET', CybusCatalogApplication[]>({
            capability: Capability.SERVICES_CATALOG_READ,
            method: 'GET',
            path: '/api/marketplace/apps',
            authenticate: true,
            handlers: {
                200: async (response) => {
                    const files = await response.getJson()

                    return files.reduce<CybusCatalogApplication[]>((prev, service) => {
                        const category = serviceCategoriesMap[service.category]

                        if (category) {
                            prev.push({
                                available: service.availableInLicense,
                                category,
                                name: service.name,
                                icon: service.icon || null,
                                provider: service.provider,
                                template: service.isTemplate,
                                directory: service.directory,
                                filename: service.filename,
                                description: service.description,
                                homepage: service.homepage,
                                version: service.version,
                                updatedAt: new Date(service.updatedAt),
                            })
                        } else {
                            this.logger.warn('Could not find category for catalog service', {
                                category: service.category,
                                serviceCategories: objectKeys(serviceCategoriesMap),
                            })
                        }

                        return prev
                    }, [])
                },
            },
        })
    }

    fetchCommissioningFile ({ name, directory, filename }: Pick<CybusCatalogApplication, 'name' | 'directory' | 'filename'>): Promise<File> {
        return this.request<'/api/marketplace/app', 'GET', File>({
            capability: Capability.SERVICES_CATALOG_READ,
            method: 'GET',
            path: '/api/marketplace/app',
            authenticate: true,
            queryParams: { directory, filename },
            handlers: {
                200: async (response) => {
                    const content = await response.getJson()
                    assert(content, new ConnectwareError(ConnectwareErrorType.SERVER_ERROR, 'Could not load commissioning file content'))

                    const extension = getNonEmptyArrayLastElement(this.configuration.getSupportedFileExtensions(FileExtensionType.SERVICE_COMMISSIONING))
                    const type = this.configuration.getServiceCommissioningFileDownloadMimeType()

                    return new File([decodeFromBase64(content.data)], `${name}.${extension}`, { type })
                },
                400: () => new ConnectwareError(ConnectwareErrorType.BAD_REQUEST, 'Unexpected Bad Request detected'),
            },
        })
    }
}
